import React, { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon, Plus, Home, BookOpen, Briefcase, FileText } from 'lucide-react';

const Navbar = ({ activePage, setActivePage, isAdmin, darkMode, toggleDarkMode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll event to add shadow when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: 'Home', icon: <Home size={18} /> },
    { id: 'blog', label: 'Blog', icon: <BookOpen size={18} /> },
    { id: 'portfolio', label: 'Portfolio', icon: <Briefcase size={18} /> },
    { id: 'cv', label: 'CV', icon: <FileText size={18} /> },
  ];

  const adminItems = [
    { id: 'new-post', label: 'New Post', icon: <Plus size={18} /> },
    { id: 'new-portfolio', label: 'New Project', icon: <Plus size={18} /> },
  ];

  const handleNavClick = (pageId) => {
    setActivePage(pageId);
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 py-3 transition-all duration-300 backdrop-blur-md ${
        darkMode ? 'bg-gray-900/90' : 'bg-white/90'
      } ${isScrolled ? `shadow-lg ${darkMode ? 'shadow-purple-500/10' : 'shadow-indigo-200/60'}` : ''} border-b ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <span className={`font-bold text-xl md:text-2xl ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              BA<span className="text-gradient font-extrabold"> | </span> <span className="hidden sm:inline">Software Engineer</span>
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <ul className="flex space-x-1">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className={`flex items-center space-x-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activePage === item.id
                        ? darkMode
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-300/40'
                        : darkMode
                        ? 'text-gray-300 hover:bg-gray-800/70 hover:text-white hover:scale-105'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:scale-105'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}

              {/* Admin-only buttons */}
              {isAdmin && adminItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className={`flex items-center space-x-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activePage === item.id
                        ? darkMode
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-300/40'
                        : darkMode
                        ? 'text-gray-300 hover:bg-gray-800/70 hover:text-white hover:scale-105'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:scale-105'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`ml-2 p-2.5 rounded-full transition-all duration-300 hover:scale-110 ${
                darkMode ? 'bg-gray-800/70 hover:bg-gray-700 shadow-lg shadow-purple-500/20' : 'bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 shadow-md'
              }`}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-gray-700" />}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 mr-2 rounded-full transition-all duration-300 hover:scale-110 ${
                darkMode ? 'bg-gray-800/70 hover:bg-gray-700 shadow-lg shadow-purple-500/20' : 'bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 shadow-md'
              }`}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-gray-700" />}
            </button>
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md ${
                darkMode ? 'text-white hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className={`md:hidden mt-3 p-3 rounded-lg fade-in-down ${darkMode ? 'bg-gray-800/90 backdrop-blur-md border border-gray-700' : 'bg-white/90 backdrop-blur-md border border-gray-200 shadow-lg'}`}>
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className={`flex items-center w-full space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activePage === item.id
                        ? darkMode
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                        : darkMode
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}

              {/* Admin-only buttons */}
              {isAdmin && adminItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className={`flex items-center w-full space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activePage === item.id
                        ? darkMode
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                        : darkMode
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Navbar;
